import type { GenerationId, ResourceId } from '@/shared/types'
import type { ResourceType, LoraWeight } from '@/shared/types/domain'

export const GENERATION_STATUS = {
  PENDING: 'pending',
  QUEUED: 'queued',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
} as const

export type GenerationStatus = typeof GENERATION_STATUS[keyof typeof GENERATION_STATUS]

// Re-exported from shared domain types
export { RESOURCE_TYPE } from '@/shared/types/domain'
export type { ResourceType } from '@/shared/types/domain'

/** Backward-compat alias — identical to LoraWeight from shared. */
export type LoraInput = LoraWeight

export interface PAGConfig {
  readonly scale: number
}

export interface FreeUConfig {
  readonly b1: number
  readonly b2: number
  readonly s1: number
  readonly s2: number
}

export interface UpscaleImage {
  readonly filename: string
  readonly previewUrl: string
}

export interface GenerationRequest {
  readonly checkpoint: string
  readonly positive_prompt: string
  readonly negative_prompt: string
  readonly width: number
  readonly height: number
  readonly sampler_name: string
  readonly scheduler: string
  readonly steps: number
  readonly cfg_scale: number
  readonly seed: number
  readonly loras: readonly LoraInput[]
  readonly pag: PAGConfig | null
  readonly freeu: FreeUConfig | null
  readonly upscale_factor: number
}

export interface GenerationResponse {
  readonly generation_id: string
  readonly status: GenerationStatus
}

export interface GenerationStatusResponse {
  readonly id: string
  readonly status: GenerationStatus
  readonly result_path: string | null
  readonly error_message: string | null
  readonly created_at: string
  readonly updated_at: string
}

export interface GenerationListResponse {
  readonly generations: readonly GenerationStatusResponse[]
  readonly total: number
}

export interface SystemStatsResponse {
  readonly system_stats: Record<string, unknown>
}

export interface ModelsListResponse {
  readonly models: readonly string[]
  readonly resource_type: string
}

// WebSocket progress event types
export const PROGRESS_EVENT_TYPE = {
  EXECUTION_START: 'execution_start',
  EXECUTION_CACHED: 'execution_cached',
  EXECUTING: 'executing',
  PROGRESS: 'progress',
  PREVIEW: 'preview',
  EXECUTED: 'executed',
  EXECUTION_ERROR: 'execution_error',
  EXECUTION_INTERRUPTED: 'execution_interrupted',
} as const

export type ProgressEventType = typeof PROGRESS_EVENT_TYPE[keyof typeof PROGRESS_EVENT_TYPE]

export interface ProgressEvent {
  readonly type: ProgressEventType
  readonly generation_id: string
  readonly progress_percent: number | null
  readonly preview_url: string | null
  readonly result_path: string | null
  readonly error_message: string | null
}

export interface ImageUploadResponse {
  readonly filename: string
  readonly subfolder: string
  readonly type: string
}

export interface FollowUpRequest {
  readonly follow_up_type: 'upscale' | 'hires_fix'
  readonly upscale_factor: number
  readonly seed: number
  readonly steps: number
  readonly cfg_scale: number
}
